import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import {
  comAtencaoDaZona,
  comReceitaDoVendedor,
  filtroDaLinha,
  filtrosDaReceita,
  filtrosDoRateio,
  linhasDaCategoria,
  precisaAtencaoDaZona,
  precisaReceitaDoVendedor,
  zonasDaReceitaDoVendedor,
} from "../pages/revenue/categoriaDaV2";
import useApiV2, {
  CINCO_MINUTOS,
  repetirSoErroDeServidor,
} from "../utils/useApiV2";

export default function useCategoriasV2({
  branchId,
  start,
  end,
  filtros,
  modoOuran,
  enabled = true,
}) {
  const {
    getRevenueMetrics,
    getZoneAllocation,
    getAttentionMetrics,
    getStockAvailable,
  } = useApiV2();
  const { user } = useAuth();
  const ativo = Boolean(enabled && branchId && start && end);
  const comZona = Boolean(filtros?.zone_id?.length);
  const base = { branch_id: branchId, start, end };

  const consulta = (nome, buscar, params, ligado = ativo) => ({
    queryKey: ["categoriasV2", nome, user?.user_id ?? null, params],
    queryFn: () => buscar(params),
    enabled: ligado,
    staleTime: CINCO_MINUTOS,
    retry: repetirSoErroDeServidor,
  });

  const [receita, rateio, atencao, estoque] = useQueries({
    queries: [
      consulta("receita", getRevenueMetrics, {
        ...base,
        tipo: "presencial",
        group_by: "category",
        ...filtrosDaReceita(filtros),
      }),
      consulta("rateio", getZoneAllocation, {
        ...base,
        tipo: "presencial",
        group_by: "collection_zone",
        ...filtrosDoRateio(filtros),
      }),
      consulta(
        "atencao",
        getAttentionMetrics,
        { ...base, group_by: "category" },
        ativo && !comZona
      ),
      consulta("estoque", getStockAvailable, { branch_id: branchId }),
    ],
  });

  const obrigatorias = comZona ? [receita, rateio] : [receita, rateio, atencao];
  const comErro = obrigatorias.find((item) => item.isError);
  const prontas =
    obrigatorias.every((item) => item.data) &&
    Boolean(estoque.data || estoque.isError);

  const linhas = useMemo(
    () =>
      prontas
        ? linhasDaCategoria({
            receita: receita.data,
            rateio: rateio.data,
            atencao: comZona ? null : atencao.data,
            estoque: estoque.data ?? null,
            filtros,
            modoOuran,
          })
        : undefined,
    [
      prontas,
      receita.data,
      rateio.data,
      atencao.data,
      estoque.data,
      filtros,
      modoOuran,
      comZona,
    ]
  );

  const linhasDaZona = useMemo(
    () =>
      (linhas || []).filter((linha) => precisaAtencaoDaZona(linha, filtros)),
    [linhas, filtros]
  );

  const atencoesDaZona = useQueries({
    queries: linhasDaZona.map((linha) =>
      consulta("atencao-na-zona", getAttentionMetrics, {
        ...base,
        ...filtrosDoRateio(filtros),
        ...filtroDaLinha(linha),
      })
    ),
  });
  const zonaComErro = atencoesDaZona.find((item) => item.isError);
  const zonaPronta = atencoesDaZona.every((item) => item.data);

  const linhasDoVendedor = useMemo(
    () =>
      (linhas || []).filter((linha) =>
        precisaReceitaDoVendedor(linha, filtros)
      ),
    [linhas, filtros]
  );

  const receitasDoVendedor = useQueries({
    queries: linhasDoVendedor.map((linha) =>
      consulta("receita-em-zona", getRevenueMetrics, {
        ...base,
        tipo: "presencial",
        zone_id: zonasDaReceitaDoVendedor(linha, filtros),
        ...filtrosDaReceita(filtros),
        ...filtroDaLinha(linha),
      })
    ),
  });
  const vendedorComErro = receitasDoVendedor.find((item) => item.isError);
  const vendedorPronto = receitasDoVendedor.every((item) => item.data);

  const data = useMemo(() => {
    if (!linhas || !vendedorPronto || !zonaPronta) return undefined;
    let resultado = linhas;
    if (linhasDaZona.length) {
      const atencaoPorChave = {};
      linhasDaZona.forEach((linha, indice) => {
        atencaoPorChave[linha.key] =
          atencoesDaZona[indice]?.data?.branches?.[0]?.pessoa_minuto ?? 0;
      });
      resultado = comAtencaoDaZona(resultado, atencaoPorChave);
    }
    if (linhasDoVendedor.length) {
      const receitaPorChave = {};
      linhasDoVendedor.forEach((linha, indice) => {
        receitaPorChave[linha.key] =
          receitasDoVendedor[indice]?.data?.totals?.bruto_vendas ?? 0;
      });
      resultado = comReceitaDoVendedor(resultado, receitaPorChave, filtros);
    }
    return resultado;
  }, [
    linhas,
    linhasDaZona,
    atencoesDaZona,
    zonaPronta,
    linhasDoVendedor,
    receitasDoVendedor,
    vendedorPronto,
    filtros,
  ]);

  const erro =
    comErro?.error ?? zonaComErro?.error ?? vendedorComErro?.error ?? null;

  return {
    data: erro ? undefined : data,
    isLoading: ativo && !erro && data === undefined,
    error: erro,
    produtosSemCategoria: receita.data?.produtos_sem_categoria ?? [],
  };
}
